import type { Observation, Procedure, Quantity } from "@medplum/fhirtypes";
import {
  DIAGNOSTIC_CHOICE_CODE_SYSTEM,
  DIAGNOSTIC_EXECUTION_DECLARATION_SNAPSHOT_URL,
  DIAGNOSTIC_OBSERVATION_COMPONENT_CODE_SYSTEM,
  DIAGNOSTIC_OBSERVATION_DEFINITION_CODE_SYSTEM,
  DIAGNOSTIC_OBSERVATION_ID_SYSTEM,
  DIAGNOSTIC_OBSERVATION_KIND,
  DIAGNOSTIC_OBSERVATION_VALUE_SOURCE_URL,
  DIAGNOSTIC_TRIAL_KIND,
  DIAGNOSTIC_TRIAL_SOURCE_URL,
  DIAGNOSTIC_UNCAPTURED_ID_SYSTEM,
  DIAGNOSTIC_UNCAPTURED_KIND,
  hasDiagnosticResourceKind,
} from "@repo/api";
import type { AnsweredValue, MemberId, ProcedureTrial, UnitValue } from "@repo/domain";
import {
  DIAGNOSTIC_PROCEDURE_ENTITY_ID_SYSTEM,
  DIAGNOSTIC_TRIAL_ID_SYSTEM,
  PATIENT_DOCUMENT_ENTITY_ID_SYSTEM,
} from "@repo/fhir-systems/identifiers";
import { procedureLegacyStatus } from "../lib/procedure-lifecycle";
import { readProcedureTrial } from "./treatment-procedure-storage";

/**
 * Where a charted diagnostic's `Procedure` + `Observation` graph keeps what the
 * order-set projection reads (ADR-0021: views read FHIR rows directly and never
 * import codecs).
 *
 * ONLY WHAT IS READ. The stored procedure also carries its patient, visit and
 * order link; none of that reaches the card, so none of it is read here.
 * Reading a field nothing uses only invents a way for a renderable row to fail.
 * The same reason keeps `performedPeriod` out: the lifecycle's timestamps matter
 * to the aggregate, and the card shows the status alone. The frozen declaration
 * snapshot is read by `readPinnedDiagnosticDeclaration` — not for the card, but
 * for the optimistic charting writes, which is the one place the client has to
 * know what the procedure was pinned to.
 *
 * A row whose status, identity or stored value this module cannot read THROWS.
 * A substituted value is indistinguishable from a real one, so a measurement
 * with no `value[x]` would silently chart as empty text.
 */

export type DiagnosticProcedureLifecycle =
  | "InProgress"
  | "Performed"
  | "UnableToComplete"
  | "DidNotPerform"
  | "EnteredInError";

export interface DiagnosticProcedureObservationRow {
  trialId?: string;
  observationId: string;
  definitionId: string;
  observedAt: string;
  values: Array<{ componentId: string; value: AnsweredValue }>;
  note?: string;
  lifecycle: "Final" | "Corrected" | "EnteredInError";
  /**
   * The filed document this reading was read OFF, when it was not typed in,
   * and whether the CURRENT value is still that document's.
   */
  derivedFrom?: { documentId: string; writtenBy: "Document" | "Hand" };
}

export interface DiagnosticProcedureRow {
  id: string;
  lifecycle: DiagnosticProcedureLifecycle;
  observations: DiagnosticProcedureObservationRow[];
  trials: ProcedureTrial[];
  /** Declaration ids that were attempted and could not be captured. */
  uncaptured: string[];
  notes?: string;
}

/**
 * FHIR's event statuses and the domain's lifecycle do not line up by name, and
 * a status outside this table is an unreadable row rather than "in progress".
 */

/**
 * The same for a measurement. Collapsing an unrecognised status to `Final`
 * charted a row nobody could read as one somebody had signed off.
 */
const OBSERVATION_LIFECYCLE_BY_STATUS: Readonly<Record<string, DiagnosticProcedureObservationRow["lifecycle"]>> = {
  final: "Final",
  corrected: "Corrected",
  "entered-in-error": "EnteredInError",
};

// Deliberately not `Procedure/<id>`: that spelling is a FHIR reference, and this
// is a row locator in a message about a row that could not be read.
function unreadable(kind: string, id: string | undefined, field: string, value?: unknown): never {
  throw new Error(
    `Diagnostic ${kind} row '${id ?? "<unassigned>"}' has no readable ${field}${
      value === undefined ? "" : ` (${JSON.stringify(value)})`
    }`,
  );
}

function identityOf(row: Observation, system: string): string | undefined {
  return row.identifier?.find((identifier) => identifier.system === system)?.value;
}

function codeOf(row: Observation, system: string): string | undefined {
  return row.code?.coding?.find((coding) => coding.system === system)?.code;
}

function unitFrom(quantity: Quantity | undefined): UnitValue | undefined {
  if (!quantity?.code) return undefined;
  return { code: quantity.code, display: quantity.unit ?? quantity.code };
}

type ValueSlots = Pick<
  Observation,
  "valueString" | "valueQuantity" | "valueInteger" | "valueBoolean" | "valueCodeableConcept"
>;

/**
 * The stored `value[x]` slot decides the kind, and a slot holding none of them
 * is a malformed row rather than empty text. Text is read last because it is
 * the only kind whose absence is indistinguishable from a blank answer. A
 * reading that genuinely stored `valueString: ""` still reads back as `Text ""`.
 */
function readValue(row: Observation, source: ValueSlots): AnsweredValue {
  if (source.valueQuantity?.value !== undefined) {
    const unit = unitFrom(source.valueQuantity);
    return { kind: "Quantity", value: source.valueQuantity.value, ...(unit === undefined ? {} : { unit }) };
  }
  if (source.valueInteger !== undefined) return { kind: "Integer", value: source.valueInteger };
  if (source.valueBoolean !== undefined) return { kind: "Boolean", value: source.valueBoolean };
  if (source.valueCodeableConcept) {
    return {
      kind: "Choice",
      selected: (source.valueCodeableConcept.coding ?? []).flatMap((coding) =>
        coding.system === DIAGNOSTIC_CHOICE_CODE_SYSTEM && coding.code ? [coding.code as MemberId] : [],
      ),
      ...(source.valueCodeableConcept.text ? { freeText: source.valueCodeableConcept.text } : {}),
    };
  }
  if (source.valueString === undefined) unreadable("observation", row.id, "recorded value");
  return { kind: "Text", value: source.valueString };
}

function componentIdOf(row: Observation, source: { code?: { coding?: { system?: string; code?: string }[] } }): string {
  const code = source.code?.coding?.find(
    (coding) => coding.system === DIAGNOSTIC_OBSERVATION_COMPONENT_CODE_SYSTEM,
  )?.code;
  return code ?? unreadable("observation", row.id, "component identity");
}

/**
 * Where the value came FROM, and whether it is still that document's. Matched
 * on the dual-written identifier rather than on the locator: the locator is a
 * resource id, and the card renders a document the client knows by its domain
 * id. An entry under any other system belongs to someone else's link and is not
 * this one — read past it rather than render an id that resolves to nothing.
 *
 * The qualifier inside our entry marks a DEPARTURE, so a link carrying none is
 * a plain link: the number came off the file and nobody has moved it. The
 * lifecycle cannot answer this — an overwrite from the file is `corrected` too.
 */
function derivedFromOf(row: Observation): DiagnosticProcedureObservationRow["derivedFrom"] {
  const ours = row.derivedFrom?.find((reference) => reference.identifier?.system === PATIENT_DOCUMENT_ENTITY_ID_SYSTEM);
  const documentId = ours?.identifier?.value;
  if (documentId === undefined) return undefined;
  const code = ours?.extension?.find(
    (candidate) => candidate.url === DIAGNOSTIC_OBSERVATION_VALUE_SOURCE_URL,
  )?.valueCode;
  return { documentId, writtenBy: code === "hand" ? "Hand" : "Document" };
}

/**
 * TWO SPELLINGS, chosen on write by the DECLARATION rather than by the data: a
 * declaration with one component and a reading with one value store into the
 * Observation's own `value[x]`, anything wider into `component[]`. Reading back
 * needs no declaration — the presence of `component[]` says which was used.
 */
function readObservation(row: Observation): DiagnosticProcedureObservationRow {
  const note = row.note?.[0]?.text;
  const derivedFrom = derivedFromOf(row);
  const trialId = row.derivedFrom?.find((ref) => ref.identifier?.system === DIAGNOSTIC_TRIAL_ID_SYSTEM)?.identifier
    ?.value;
  return {
    observationId:
      identityOf(row, DIAGNOSTIC_OBSERVATION_ID_SYSTEM) ?? unreadable("observation", row.id, "measurement identity"),
    definitionId:
      codeOf(row, DIAGNOSTIC_OBSERVATION_DEFINITION_CODE_SYSTEM) ??
      unreadable("observation", row.id, "declaration identity"),
    observedAt: row.effectiveDateTime ?? unreadable("observation", row.id, "observed instant"),
    values: row.component?.length
      ? row.component.map((component) => ({
          componentId: componentIdOf(row, component),
          value: readValue(row, component),
        }))
      : [{ componentId: componentIdOf(row, row), value: readValue(row, row) }],
    ...(note === undefined ? {} : { note }),
    ...(derivedFrom === undefined ? {} : { derivedFrom }),
    ...(trialId ? { trialId } : {}),
    lifecycle:
      OBSERVATION_LIFECYCLE_BY_STATUS[row.status ?? ""] ?? unreadable("observation", row.id, "status", row.status),
  };
}

export function readDiagnosticProcedure(anchor: Procedure, children: readonly Observation[]): DiagnosticProcedureRow {
  const identity = anchor.identifier?.find(
    (identifier) => identifier.system === DIAGNOSTIC_PROCEDURE_ENTITY_ID_SYSTEM,
  )?.value;
  const notes = anchor.note?.[0]?.text;
  return {
    id: identity ?? unreadable("procedure", anchor.id, "procedure identity"),
    lifecycle: procedureLegacyStatus(anchor.status) ?? unreadable("procedure", anchor.id, "status", anchor.status),
    trials: children
      .filter((row) => hasDiagnosticResourceKind(row, DIAGNOSTIC_TRIAL_KIND))
      .map((row) => readProcedureTrial(row, DIAGNOSTIC_TRIAL_ID_SYSTEM, DIAGNOSTIC_TRIAL_SOURCE_URL))
      .sort((a, b) => Date.parse(a.effectiveDateTime) - Date.parse(b.effectiveDateTime)),
    observations: children
      .filter((row) => hasDiagnosticResourceKind(row, DIAGNOSTIC_OBSERVATION_KIND))
      .map(readObservation),
    // A struck uncaptured slot asserts nothing, so the declaration reads as open
    // again rather than as one somebody failed to capture.
    uncaptured: children
      .filter((row) => hasDiagnosticResourceKind(row, DIAGNOSTIC_UNCAPTURED_KIND) && row.status !== "entered-in-error")
      .map(
        (row) =>
          identityOf(row, DIAGNOSTIC_UNCAPTURED_ID_SYSTEM) ??
          unreadable("uncaptured observation", row.id, "declaration identity"),
      ),
    ...(notes === undefined ? {} : { notes }),
  };
}

/**
 * The exact DiagnosticDefinition revision frozen onto the procedure when its
 * execution began. The optimistic charting writes read it to spell a
 * measurement's FHIR `code` and to choose between the single-value and the
 * `component[]` layout — the one stored thing the client cannot get from the
 * catalog, because the catalog has moved on and the pinned revision has not.
 */
export interface PinnedDiagnosticDeclaration {
  definitionUrl: string;
  version: number;
  name: string;
  observations: readonly {
    id: string;
    label: string;
    components: readonly { id: string; label: string }[];
  }[];
}

/**
 * The snapshot is our own JSON in a `valueString`, so `JSON.parse` hands back
 * `any` and a cast to the interface above asserts a shape nothing checked.
 *
 * ONLY THE FIELDS THIS SIDE READS. The stored declaration is wider — each
 * component carries a measurement `value`, each observation a `repeatable`
 * flag — and the write side's own reader
 * (`packages/api/src/engine/codecs/diagnostic-procedure.codec.ts`) walks all of
 * it before the domain validates it. Restating that walk here would be a second
 * copy free to drift; the fields below are the ones this package's type already
 * promises, and checking them is what makes that promise true rather than
 * asserted. ADR-0021 keeps the two apart: views read FHIR rows directly and
 * never import a codec.
 *
 * A field that moved or changed type is a LOUD, row-locating throw. Substituting
 * a default would chart a measurement under the wrong code, which reconciles
 * into the record looking exactly like one somebody meant.
 */
export function readPinnedDiagnosticDeclaration(anchor: Procedure): PinnedDiagnosticDeclaration {
  const encoded = anchor.extension?.find(
    (candidate) => candidate.url === DIAGNOSTIC_EXECUTION_DECLARATION_SNAPSHOT_URL,
  )?.valueString;
  if (encoded === undefined) unreadable("procedure", anchor.id, "pinned declaration");
  let parsed: unknown;
  try {
    parsed = JSON.parse(encoded);
  } catch {
    unreadable("procedure", anchor.id, "pinned declaration", encoded);
  }
  const declaration = objectAt(anchor, parsed, "pinned declaration");
  return {
    definitionUrl: stringAt(anchor, declaration, "definitionUrl", "pinned declaration definitionUrl"),
    version: numberAt(anchor, declaration, "version", "pinned declaration version"),
    name: stringAt(anchor, declaration, "name", "pinned declaration name"),
    observations: arrayAt(anchor, declaration, "observations", "pinned declaration observations").map(
      (entry, index) => {
        const at = `pinned declaration observations[${index}]`;
        const observation = objectAt(anchor, entry, at);
        return {
          id: stringAt(anchor, observation, "id", `${at}.id`),
          label: stringAt(anchor, observation, "label", `${at}.label`),
          components: arrayAt(anchor, observation, "components", `${at}.components`).map((value, position) => {
            const componentAt = `${at}.components[${position}]`;
            const component = objectAt(anchor, value, componentAt);
            return {
              id: stringAt(anchor, component, "id", `${componentAt}.id`),
              label: stringAt(anchor, component, "label", `${componentAt}.label`),
            };
          }),
        };
      },
    ),
  };
}

function objectAt(row: Procedure, value: unknown, field: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value))
    unreadable("procedure", row.id, field, value);
  return value as Record<string, unknown>;
}

function arrayAt(row: Procedure, holder: Record<string, unknown>, key: string, field: string): unknown[] {
  const value = holder[key];
  if (!Array.isArray(value)) unreadable("procedure", row.id, field, value);
  return value;
}

function stringAt(row: Procedure, holder: Record<string, unknown>, key: string, field: string): string {
  const value = holder[key];
  if (typeof value !== "string") unreadable("procedure", row.id, field, value);
  return value;
}

function numberAt(row: Procedure, holder: Record<string, unknown>, key: string, field: string): number {
  const value = holder[key];
  if (typeof value !== "number") unreadable("procedure", row.id, field, value);
  return value;
}
